import { Box, Button, Slider, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { useCustomSnackbar } from 'components/NotificationSnackbars';
import { VideoTrimSliderProps } from './types';
import { useVideoTrimSliderStyles } from './styles';
import { formatNumberToNDigits } from 'kineticUtils';

const VideoTrimSlider = ({ videoDuration, url, fileName }: VideoTrimSliderProps) => {
  const { t } = useTranslation('common');
  const { enqueueCustomSnackbar } = useCustomSnackbar();
  const [trimRange, setTrimRange] = useState([0, videoDuration]);
  const [ffmpegLoaded, setFFmpegLoaded] = useState(false);
  const [trimmingInProgress, setTrimmingInProgress] = useState(false);
  const ffmpegRef = useRef<FFmpeg>(new FFmpeg());
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const {
    videoTrimWrapper,
    sliderWrapper,
    customSliderRoot,
    customSliderRail,
    customSliderTrack,
    customSliderThumb,
    videoDurationLabel,
  } = useVideoTrimSliderStyles();
  const [start, end] = trimRange;

  const initializeFFmpeg = useCallback(async () => {
    try {
      await ffmpegRef.current.load();
      setFFmpegLoaded(true);
    } catch {
      enqueueCustomSnackbar.error(t('failedFFmpeg'));
    }
  }, [enqueueCustomSnackbar, t]);

  useEffect(() => {
    initializeFFmpeg();
    setTrimRange([0, videoDuration]);

    return () => {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    };
  }, [initializeFFmpeg, videoDuration]);

  const formatDuration = (seconds: number) => {
    const minutes = formatNumberToNDigits(Math.floor(seconds / 60), 2);
    const remainingSeconds = formatNumberToNDigits(Math.floor(seconds % 60), 2);
    return `${minutes}:${remainingSeconds}`;
  };

  const handleTrim = async () => {
    if (!ffmpegLoaded) {
      enqueueCustomSnackbar.warning(t('ffmpegNotLoaded'));
      return;
    }

    setTrimmingInProgress(true);

    const signal = abortControllerRef.current.signal;

    try {
      const response = await fetch(url, { signal });
      const videoData = await response.arrayBuffer();
      await ffmpegRef.current.writeFile(`input_${fileName}`, new Uint8Array(videoData));

      await ffmpegRef.current.exec([
        '-ss',
        start.toString(),
        '-i',
        `input_${fileName}`,
        '-t',
        (end - start).toString(),
        '-c',
        'copy',
        `output_${fileName}`,
      ]);

      const outputData = await ffmpegRef.current.readFile(`output_${fileName}`);
      FileSaver.saveAs(
        new Blob([outputData as ArrayBufferView<ArrayBuffer>]),
        fileName || 'trimmedVideo'
      );
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        enqueueCustomSnackbar.error(t('trimmingFailed'));
      } else {
        enqueueCustomSnackbar.error(t('trimmingAborted'));
      }
    } finally {
      setTrimmingInProgress(false);
    }
  };
  const isDurationExceedsOneHour = end - start > 3600;
  const trimmingButton = !ffmpegLoaded || trimmingInProgress || isDurationExceedsOneHour;
  return (
    <div className={videoTrimWrapper}>
      <Box className={sliderWrapper}>
        <Slider
          value={trimRange}
          onChange={(e, newValue) => setTrimRange(newValue as number[])}
          min={0}
          max={videoDuration}
          valueLabelDisplay='off'
          classes={{
            root: customSliderRoot,
            rail: customSliderRail,
            track: customSliderTrack,
            thumb: customSliderThumb,
          }}
        />
        <Typography
          variant='caption'
          className={videoDurationLabel}
          sx={{
            left: `${(trimRange[0] / videoDuration) * 100}%`,
          }}
        >
          {formatDuration(trimRange[0])}
        </Typography>
        <Typography
          variant='caption'
          className={videoDurationLabel}
          sx={{
            left: `${(trimRange[1] / videoDuration) * 100}%`,
          }}
        >
          {formatDuration(trimRange[1])}
        </Typography>
      </Box>
      <Tooltip
        title={isDurationExceedsOneHour && t('videoLessThan1hour')}
        disableHoverListener={!trimmingButton}
      >
        <span>
          <Button
            variant='contained'
            size='small'
            color='secondary'
            onClick={handleTrim}
            disabled={trimmingButton}
          >
            {trimmingInProgress ? t('trimmingVideo') : t('trimVideo')}
          </Button>
        </span>
      </Tooltip>
    </div>
  );
};

export default VideoTrimSlider;
